import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { supabase } from '../lib/supabase'

const MODULE_LABELS = {
  loan: 'Loan Receipt', loan_repayment: 'Loan Repayment',
  interest: 'Interest Accrual', interest_payment: 'Interest Payment',
  equity: 'Equity',
  fixed_asset: 'Fixed Asset Purchase', fixed_asset_payment: 'Asset Payment',
  fixed_asset_capitalise: 'Asset Capitalisation', fixed_asset_sync: 'Asset Sync',
  payables: 'Supplier Payment', supplier_invoice: 'Supplier Invoice',
  invoice: 'Customer Invoice', customer_payment: 'Customer Payment',
  cash_expense: 'Cash Expense', depreciation: 'Depreciation',
  inventory: 'Inventory', inventory_wip: 'WIP Transfer',
  inventory_finished: 'Finished Goods', cogs: 'Cost of Sales', payroll: 'Payroll',
}

const MODULE_COLORS = {
  loan: 'badge-blue', loan_repayment: 'badge-red',
  interest: 'badge-amber', interest_payment: 'badge-red',
  equity: 'badge-amber',
  fixed_asset: 'badge-green', fixed_asset_payment: 'badge-green',
  fixed_asset_capitalise: 'badge-green',
  payables: 'badge-red', invoice: 'badge-blue',
  customer_payment: 'badge-green', cash_expense: 'badge-red',
  depreciation: 'badge-grey',
}

const ugandaToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
const fmt = (n) => Math.round(n).toLocaleString()

export default function JournalLedger() {
  const [journals, setJournals] = useState([])
  const [coaMap, setCoaMap] = useState({})
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [search, setSearch] = useState('')
  const [moduleFilter, setModuleFilter] = useState('')
  const [accountFilter, setAccountFilter] = useState('')

  const now = new Date()
  const [startDate, setStartDate] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`)
  const [endDate, setEndDate] = useState(ugandaToday())

  useEffect(() => { loadData() }, [startDate, endDate])

  const loadData = async () => {
    setLoading(true)
    const [jeRes, coaRes] = await Promise.all([
      supabase.from('journal_entries').select('*')
        .gte('entry_date', startDate).lte('entry_date', endDate)
        .order('entry_date', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('chart_of_accounts').select('code,name,category'),
    ])
    const map = {}
    ;(coaRes.data || []).forEach(a => { map[a.code] = { name: a.name, category: a.category } })
    setCoaMap(map)
    setJournals(jeRes.data || [])
    setLoading(false)
  }

  const accountLabel = (code) => {
    const a = coaMap[code]
    return a ? `${code} — ${a.name}` : code
  }

  const filtered = journals.filter(j => {
    const s = search.toLowerCase()
    return (!search || j.description?.toLowerCase().includes(s) || j.journal_id?.toLowerCase().includes(s) || j.reference?.toLowerCase().includes(s))
      && (!moduleFilter || j.module === moduleFilter)
      && (!accountFilter || j.debit_account === accountFilter || j.credit_account === accountFilter)
  })

  const totalAmount = filtered.reduce((s, j) => s + j.amount, 0)
  const modules = [...new Set(journals.map(j => j.module))].sort()
  const accounts = [...new Set([...journals.map(j => j.debit_account), ...journals.map(j => j.credit_account)])].sort()

  const handleExportExcel = async () => {
    if (!window.XLSX) {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'
        s.onload = resolve; s.onerror = reject
        document.head.appendChild(s)
      })
    }
    const XLSX = window.XLSX
    const wb = XLSX.utils.book_new()

    // Sheet 1 — All journal entries
    const rows1 = [
      ['Kobflo Greentek Ltd — Journal Ledger'],
      [`Period: ${startDate} to ${endDate}   |   Generated: ${ugandaToday()}`],
      [],
      ['Journal ID', 'Date', 'Module', 'Reference', 'Dr Code', 'Dr Account Name', 'Cr Code', 'Cr Account Name', 'Amount (UGX)', 'Description'],
      ...filtered.map(j => [
        j.journal_id, j.entry_date,
        MODULE_LABELS[j.module] || j.module,
        j.reference || '',
        j.debit_account, coaMap[j.debit_account]?.name || '',
        j.credit_account, coaMap[j.credit_account]?.name || '',
        j.amount, j.description || '',
      ]),
      [],
      ['', '', '', '', '', '', '', 'TOTAL', totalAmount, ''],
    ]
    const ws1 = XLSX.utils.aoa_to_sheet(rows1)
    ws1['!cols'] = [{ wch:28 },{ wch:12 },{ wch:22 },{ wch:18 },{ wch:8 },{ wch:30 },{ wch:8 },{ wch:30 },{ wch:18 },{ wch:50 }]
    ws1['!merges'] = [{ s:{r:0,c:0}, e:{r:0,c:9} }, { s:{r:1,c:0}, e:{r:1,c:9} }]
    XLSX.utils.book_append_sheet(wb, ws1, 'Journal Entries')

    // Sheet 2 — Account summary
    const accountSummary = {}
    filtered.forEach(j => {
      if (!accountSummary[j.debit_account]) accountSummary[j.debit_account] = { dr: 0, cr: 0 }
      if (!accountSummary[j.credit_account]) accountSummary[j.credit_account] = { dr: 0, cr: 0 }
      accountSummary[j.debit_account].dr += j.amount
      accountSummary[j.credit_account].cr += j.amount
    })
    const rows2 = [
      ['Kobflo Greentek Ltd — Account Summary by Period'],
      [`Period: ${startDate} to ${endDate}`],
      [],
      ['Account Code', 'Account Name', 'Category', 'Total Debits (UGX)', 'Total Credits (UGX)', 'Net Movement (UGX)'],
      ...Object.entries(accountSummary).sort(([a],[b]) => a.localeCompare(b)).map(([code, bal]) => [
        code, coaMap[code]?.name || code, coaMap[code]?.category || '',
        bal.dr || 0, bal.cr || 0, bal.dr - bal.cr,
      ]),
      [],
      ['', 'TOTAL', '', totalAmount, totalAmount, 0],
    ]
    const ws2 = XLSX.utils.aoa_to_sheet(rows2)
    ws2['!cols'] = [{ wch:14 },{ wch:32 },{ wch:14 },{ wch:20 },{ wch:20 },{ wch:20 }]
    ws2['!merges'] = [{ s:{r:0,c:0}, e:{r:0,c:5} }, { s:{r:1,c:0}, e:{r:1,c:5} }]
    XLSX.utils.book_append_sheet(wb, ws2, 'Account Summary')

    XLSX.writeFile(wb, `Kobflo_JournalLedger_${startDate}_to_${endDate}.xlsx`)
  }

  return (
    <AppLayout title="Journal Ledger">
      <div className="section-header">
        <div className="section-icon">📒</div>
        <div className="section-info">
          <h2>Journal Ledger</h2>
          <p>All double-entry journal entries. Click any row to expand. Export to Excel for audit records.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">From Date</label>
            <input className="form-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">To Date</label>
            <input className="form-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Module</label>
            <select className="form-input" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
              <option value="">All modules</option>
              {modules.map(m => <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Account</label>
            <select className="form-input" value={accountFilter} onChange={e => setAccountFilter(e.target.value)}>
              <option value="">All accounts</option>
              {accounts.map(a => <option key={a} value={a}>{accountLabel(a)}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-input" placeholder="🔍 Search description, journal ID, reference..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 240, maxWidth: 420 }} />
          <button className="btn btn-secondary btn-sm" onClick={loadData}>↻ Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={handleExportExcel} disabled={filtered.length === 0}>
            📊 Export Excel
          </button>
          {(search || moduleFilter || accountFilter) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setModuleFilter(''); setAccountFilter('') }}>
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        {[
          ['Entries shown', filtered.length.toLocaleString(), 'var(--text)'],
          ['Total Debits', `UGX ${fmt(totalAmount)}`, 'var(--green-deep)'],
          ['Total Credits', `UGX ${fmt(totalAmount)}`, 'var(--text-muted)'],
        ].map(([label, value, color]) => (
          <div key={label} className="card" style={{ padding: '12px 16px', margin: 0 }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Loading journals...</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📒</div>
            <h3>No journal entries found</h3>
            <p>Adjust the date range or clear filters.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Module</th>
                  <th>Description</th>
                  <th>Dr Account</th>
                  <th>Cr Account</th>
                  <th style={{ textAlign: 'right' }}>Amount (UGX)</th>
                  <th style={{ width: 20 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <React.Fragment key={j.id}>
                    <tr
                      onClick={() => setExpanded(expanded === j.id ? null : j.id)}
                      style={{
                        cursor: 'pointer',
                        background: expanded === j.id ? 'var(--green-pale)' : 'transparent',
                        borderLeft: expanded === j.id ? '3px solid var(--green-bright)' : '3px solid transparent',
                      }}
                      onMouseEnter={e => { if (expanded !== j.id) e.currentTarget.style.background = 'var(--cream)' }}
                      onMouseLeave={e => { if (expanded !== j.id) e.currentTarget.style.background = 'transparent' }}
                    >
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                        {new Date(j.entry_date + 'T00:00:00').toLocaleDateString('en-UG')}
                      </td>
                      <td>
                        <span className={`badge ${MODULE_COLORS[j.module] || 'badge-grey'}`} style={{ fontSize: '0.65rem' }}>
                          {MODULE_LABELS[j.module] || j.module}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)', maxWidth: 200 }}>
                        {(j.description || '—').length > 50 ? j.description.slice(0,50)+'...' : j.description || '—'}
                      </td>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.72rem', color: 'var(--green-deep)' }}>
                        {j.debit_account} — {coaMap[j.debit_account]?.name || ''}
                      </td>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {j.credit_account} — {coaMap[j.credit_account]?.name || ''}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>
                        {fmt(j.amount)}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                        {expanded === j.id ? '▲' : '▼'}
                      </td>
                    </tr>

                    {expanded === j.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, background: 'var(--green-pale)' }}>
                          <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 10, marginBottom: 14 }}>
                              {[
                                ['Journal ID', j.journal_id],
                                ['Date', new Date(j.entry_date+'T00:00:00').toLocaleDateString('en-UG',{weekday:'long',year:'numeric',month:'long',day:'numeric'})],
                                ['Module', MODULE_LABELS[j.module] || j.module],
                                ['Reference', j.reference || '—'],
                                ['Source ID', j.source_id || '—'],
                                ['Recorded', j.created_at ? new Date(j.created_at).toLocaleString('en-UG') : '—'],
                              ].map(([label, val]) => (
                                <div key={label} style={{ background: 'white', borderRadius: 6, padding: '8px 12px' }}>
                                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: 2 }}>{label}</div>
                                  <div style={{ fontWeight: 500, fontSize: '0.8rem', wordBreak: 'break-all' }}>{val}</div>
                                </div>
                              ))}
                            </div>
                            {j.description && (
                              <div style={{ background: 'white', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: '0.82rem' }}>
                                <strong>Description:</strong> {j.description}
                              </div>
                            )}
                            <div style={{ background: 'white', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                              <div style={{ background: 'var(--green-deep)', padding: '7px 14px', color: 'white', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em' }}>
                                DOUBLE ENTRY
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                                {[
                                  { side: 'Debit', code: j.debit_account, color: 'var(--green-deep)' },
                                  { side: 'Credit', code: j.credit_account, color: 'var(--text-muted)' },
                                ].map(({ side, code, color }) => (
                                  <div key={side} style={{ padding: '12px 16px', borderRight: side === 'Debit' ? '1px solid var(--border)' : 'none' }}>
                                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', fontFamily: "'DM Mono',monospace" }}>{side}</div>
                                    <div style={{ fontFamily: "'DM Mono',monospace", color, fontWeight: 700, fontSize: '0.85rem' }}>{code}</div>
                                    <div style={{ fontSize: '0.8rem', marginTop: 2 }}>{coaMap[code]?.name || code}</div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{coaMap[code]?.category}</div>
                                    <div style={{ fontWeight: 700, marginTop: 8, color }}>UGX {fmt(j.amount)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                  <td colSpan={5} style={{ padding: '10px 12px' }}>{filtered.length} entries</td>
                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '10px 12px' }}>UGX {fmt(totalAmount)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
